import { Col, Row } from 'antd';
import { useState, useEffect, useRef, type FC, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { YoutubeOutlined, AudioOutlined } from '@ant-design/icons';
import { useAppSelector } from '../../../../../store/store';
import YouTube from 'react-youtube';
import { Marquee } from '../../PlayingBar/Marquee';

interface NowPlayingCardProps {
  title: string;
  albumId: string;
  extra?: any;
  children?: any;
  image?: string;
  imageTitle?: string;
  subtitle?: string | ReactNode;
  searchQuery?: string;
}

export const NowPlayingCard: FC<NowPlayingCardProps> = (props) => {
  const navigate = useNavigate();
  const [isVideo, setIsVideo] = useState(false);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const position = useAppSelector((state) => state.spotify.state?.position) || 0;
  const paused = useAppSelector((state) => state.spotify.state?.paused);
  
  const isElectron = /electron/i.test(navigator.userAgent);
  
  const playerRef = useRef<any>(null);
  const lastSeekTimeRef = useRef<number>(0);
  const showVideoTimeoutRef = useRef<any>(null);

  // When track changes, reset videoId
  useEffect(() => {
    setVideoId(null);
    setIsPlaying(false);
    setShowVideo(false);
    if (playerRef.current && typeof playerRef.current.pauseVideo === 'function') {
      playerRef.current.pauseVideo();
    }
  }, [props.searchQuery]);

  // Handle delay for showing video to mask YouTube controls
  useEffect(() => {
    if (isPlaying) {
      showVideoTimeoutRef.current = setTimeout(() => {
        setShowVideo(true);
      }, 2500); // Wait 2.5 seconds for YouTube controls to fade out
    } else {
      if (showVideoTimeoutRef.current) clearTimeout(showVideoTimeoutRef.current);
      setShowVideo(false);
    }
    return () => {
      if (showVideoTimeoutRef.current) clearTimeout(showVideoTimeoutRef.current);
    };
  }, [isPlaying]);

  // Scrape video ID
  useEffect(() => {
    if (isVideo && props.searchQuery && !videoId) {
      fetch(`https://corsproxy.io/?${encodeURIComponent(`https://www.youtube.com/results?search_query=${encodeURIComponent(props.searchQuery)}`)}`)
        .then((res) => res.text())
        .then((html) => {
          const match = html.match(/"videoId":"([a-zA-Z0-9_-]{11})"/);
          if (match && match[1]) {
            setVideoId(match[1]);
          }
        })
        .catch((err) => console.error(err));
    }
  }, [isVideo, props.searchQuery, videoId]);

  // Sync Play/Pause
  useEffect(() => {
    if (playerRef.current && typeof playerRef.current.getPlayerState === 'function') {
      const state = playerRef.current.getPlayerState();
      if (paused && state === 1) {
        playerRef.current.pauseVideo();
      } else if (!paused && state === 2) {
        playerRef.current.playVideo();
      }
    }
  }, [paused]);

  // Sync position (seek)
  useEffect(() => {
    if (playerRef.current && !paused) {
      if (typeof playerRef.current.getPlayerState === 'function') {
        const state = playerRef.current.getPlayerState();
        if (state === 3) return; // Do not seek if already buffering
        
        const currentVidTime = playerRef.current.getCurrentTime() || 0;
        const targetTime = position / 1000;
        
        if (Math.abs(currentVidTime - targetTime) > 5) {
            const now = Date.now();
            if (now - lastSeekTimeRef.current > 2000) {
                playerRef.current.seekTo(targetTime, true);
                lastSeekTimeRef.current = now;
            }
        }
      }
    }
  }, [position, paused]);

  return (
    <div className='playing-now-card'>
      {props.image ? (
        <div
          className='playing-now-card-image'
          style={{
            position: 'relative',
            cursor: 'pointer',
            backgroundImage: (isVideo && showVideo) ? 'none' : `linear-gradient(rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 50%), url("${props.image}")`,
            backgroundColor: isVideo ? '#000' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            aspectRatio: isVideo ? '16/9' : '1 / 1',
            borderRadius: 8
          }}
          onClick={(e) => {
            if (!isVideo) {
              navigate(`/album/${props.albumId}`);
            }
          }}
        >
          {isVideo && videoId ? (
            <div style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              opacity: showVideo ? 1 : 0,
              transition: 'opacity 0.8s ease-in-out',
              pointerEvents: 'none'
            }}>
              <YouTube
                videoId={videoId}
                className="youtube-iframe-absolute"
                opts={{
                  width: '100%',
                  height: '100%',
                  playerVars: {
                    autoplay: 1,
                    controls: 0,
                    disablekb: 1,
                    fs: 0,
                    iv_load_policy: 3,
                    modestbranding: 1,
                    rel: 0,
                    showinfo: 0,
                    playsinline: 1
                  }
                }}
                onReady={(event) => {
                  playerRef.current = event.target;
                  event.target.playVideo();
                }}
                onStateChange={(event) => {
                  // event.data === 1 is PLAYING. If it's anything else (PAUSED, BUFFERING), we hide it!
                  // This completely masks the ugly YouTube pause button and title bars.
                  setIsPlaying(event.data === 1);
                }}
              />
            </div>
          ) : (
            <div className='playing-now-card-image-text'>
              <div data-encore-id='text'>
                <div>{props.imageTitle}</div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {isElectron && props.searchQuery && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setIsVideo(!isVideo);
          }}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            padding: '6px 16px',
            borderRadius: 32,
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12,
            marginBottom: 4,
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)')}
        >
          {isVideo ? <AudioOutlined /> : <YoutubeOutlined />}
          {isVideo ? 'Switch to audio' : 'Switch to video'}
        </div>
      )}

      <div className='playing-now-card-text' style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
          <div style={{ flex: 1, minWidth: 0, marginRight: props.extra ? 12 : 0 }}>
            <div
              className='playing-now-card-title'
              style={props.image ? { minWidth: 0 } : { marginTop: 10, minWidth: 0 }}
            >
              <Marquee title={typeof props.title === 'string' ? props.title : undefined}>
                {props.title}
              </Marquee>
            </div>
            <div className='playing-now-card-subtitle' style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{props.subtitle}</div>
          </div>
          {props.extra ? (
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {props.extra}
            </div>
          ) : null}
        </div>
        {props.children ? <div className='playing-now-card-body'>{props.children}</div> : null}
      </div>
    </div>
  );
};
